// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.OtherSettingsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Izenda Other Settings Store
 */

import { action, observe } from 'mobx';
import { isObject } from 'lodash/fp';

import AppGlobal from 'Izenda.AppGlobal';
import fetcher from 'fetchers/Izenda.AdvancedSettingDataFetcher';
import systemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import OtherSettings from './models/Izenda.OtherSettings';
import uiState from 'logic/Izenda.UiState';
import localizer from 'share/localization/Izenda.Localizer';

export default class OtherSettingsStore {
  otherSettings = new OtherSettings();

  /**
   * link uiState and store
   */
  init(): void {
    this.observeTenantId();
    uiState.setCheckDirtyFunction(() => this.otherSettings.isDirty);

    this.otherSettings.resetErrorMessage();
    return this.getInitial(uiState.getTenantId());
  }

  /**
   * unlink between uistate and store
   */
  destroy(): void {
    this.disposeLoadOthersSetting();
    uiState.resetCheckDirtyFunction();
  }

  /**
   * Observe tenantID on setting level
   */
  observeTenantId(): void {
    this.disposeLoadOthersSetting = observe(uiState, 'tenantId', () => {
      this.getInitial(uiState.getTenantId());
    });
  }

  /**
   * Load default data
   * @param {string} tenantId
   */
  @action
  getInitial(tenantId: string): any {
    tenantId = tenantId || uiState.getTenantId();
    this.otherSettings.tenantId = tenantId;
    const assignOtherSettings = result => this.otherSettings.readOtherSettingsDetail(result);

    return fetcher.loadOther(tenantId).then(assignOtherSettings);
  }

  /**
   * Save other data
   */
  @action
  onSave(): any {
    const otherSettings = this.otherSettings;
    const showSaveResult = result => {
      let message = localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED');
      if (!result.success) {
        const messageData = result.messages[0];
        isObject(messageData) ? (message = messageData.messages) : (message = messageData);
      }
      otherSettings.alertMessage = {
        type: result.success ? 'success' : 'error',
        message: message
      };
      result.success && otherSettings.resetDirty();
    };

    const data = {
      sortColumnNameValue: otherSettings.sortColumnNameValue,
      trimTimeInJoinsValue: otherSettings.trimTimeInJoinsValue,
      timezoneForDataOffsetValue: otherSettings.timezoneForDataOffsetValue,
      timezoneForTimestampOffsetValue: otherSettings.timezoneForTimestampOffsetValue,
      convertNullToEmptyStringValue: otherSettings.convertNullToEmptyStringValue,
      showSchemaNameValue: otherSettings.showSchemaNameValue,
      showIntroductionTextValue: otherSettings.showIntroductionTextValue,
      introductionTextValue: otherSettings.introductionTextValue,
      sendToDiskPathValue: otherSettings.sendToDiskPathValue,
      commonFilterSettingValue: otherSettings.commonFilterSettingValue,
      multipleSortOnGridHeaderValue: otherSettings.multipleSortOnGridHeaderValue,
      sortColumnNameDefaultValue: otherSettings.sortColumnNameDefaultValue,
      trimTimeInJoinsDefaultValue: otherSettings.trimTimeInJoinsDefaultValue,
      timezoneForDataOffsetDefaultValue: otherSettings.timezoneForDataOffsetDefaultValue,
      timezoneForTimestampOffsetDefaultValue: otherSettings.timezoneForTimestampOffsetDefaultValue,
      convertNullToEmptyStringDefaultValue: otherSettings.convertNullToEmptyStringDefaultValue,
      showSchemaNameDefaultValue: otherSettings.showSchemaNameDefaultValue,
      showIntroductionTextDefaultValue: otherSettings.showIntroductionTextDefaultValue,
      introductionTextDefaultValue: otherSettings.introductionTextDefaultValue,
      sendToDiskPathDefaultValue: otherSettings.sendToDiskPathDefaultValue,
      commonFilterSettingDefaultValue: otherSettings.commonFilterSettingDefaultValue,
      multipleSortOnGridHeaderDefaultValue: otherSettings.multipleSortOnGridHeaderDefaultValue,
      showPreviewValue: otherSettings.showPreviewValue,
      tenantId: otherSettings.tenantId,
      modified: otherSettings.modified,
      hideReportHeaderAndFooterValue: otherSettings.hideReportHeaderAndFooterValue
    };

    return fetcher
      .saveOther(data)
      .then(showSaveResult)
      .then(this.getInitial.bind(this, uiState.getTenantId()))
      .then(AppGlobal.setOther.bind(AppGlobal, data));
  }

  /**
   * Load Other
   */
  @action
  loadOther(): any {
    return fetcher.loadOther(uiState.getTenantId());
  }

  /**
   * Load Category Setting.
   */
  loadCategorySettings(): any {
    return systemSettingFetcher.categorySetting();
  }

  /**
   * Load System Date.
   */
  loadSystemDate(): any {
    return systemSettingFetcher.loadSystemDate();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.OtherSettingsStore.js